'use client';

import React, { FC, useEffect } from 'react';
import {
  createPortableDialog,
  DialogCloseButton,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';
import { useWindowSize } from '@wwe-portal/ui/hooks/use-window-size';
import { Dialog, DialogContent } from './primitive';

const YoutubeDialogUI: FC<{
  open: boolean;
  onClose: () => void;
  url: string;
  videoId?: string | null;
}> = ({ open, onClose, url, videoId }) => {
  const { width } = useWindowSize();

  const videoWidth = width < 1440 ? 860 : 920;
  const videoHeight = width < 1440 ? 510 : 580;

  useEffect(() => {
    if (!open) return;

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';

    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag?.parentNode != null) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    function onYouTubeIframeAPIReady() {
      if (window.YT?.Player != null && videoId) {
        const player = new window.YT.Player(`youtube_${videoId}`, {
          videoId,
        });

        const iframeWindow = player.getIframe()?.contentWindow;
        const callback = (event) => {
          if (event.origin !== 'https://www.youtube.com') {
            return;
          }
          if (event.source === iframeWindow) {
            const data = JSON.parse(event.data);
            if (data?.info?.playerState === 0 && onClose) {
              onClose();
            }
          }
        };
        window.addEventListener('message', callback);
      }
    }

    window.onYouTubePlayerAPIReady = onYouTubeIframeAPIReady;

    return () => {
      window.onYouTubePlayerAPIReady = undefined;
      window.removeEventListener('message', () => {});
    };
  }, [open, onClose]);

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose?.();
      }}
      open={open}
    >
      <DialogContent
        className="max-w-full bg-none md:max-w-full 2xl:max-w-full"
        style={{
          width: videoWidth,
        }}
        variant="dialog"
      >
        <iframe
          className="max-h-full max-w-full px-5 md:px-0"
          width={videoWidth}
          height={videoHeight}
          frameBorder={0}
          src={url}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; local-network-access"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          id={`youtube_${videoId}`}
        />
        <DialogCloseButton onClose={onClose} className="right-5 md:right-0" />
      </DialogContent>
    </Dialog>
  );
};

export const YoutubeDialog = createPortableDialog(
  ({ url, videoId }: { url: string; videoId?: string | null }) => {
    const modal = usePortableDialog();
    return (
      <YoutubeDialogUI
        open={modal.visible}
        onClose={modal.remove}
        url={url}
        videoId={videoId}
      />
    );
  }
);
